import {
  LEAVE_TYPES_ALIASES,
  MATERNITY_LEAVE_SUB_TYPES,
  MATERNITY_LEAVE_SUB_TYPES_WITH_HINTS,
} from 'domain/shared/leave';
import { isEmployeeContractByType, isMinskOffice, isPolishOffice } from 'domain/shared/employee';

const BY_MATERNITY_SUBTYPE_KEYS = ['BY_MATERNITY_SICK_LEAVE', 'BY_PARENTAL_LEAVE'] as const;
const PL_MATERNITY_SUBTYPE_KEYS = [
  'PL_MATERNITY_LEAVE',
  'PL_PATERNITY_LEAVE',
  'PL_PARENTAL_LEAVE',
  'PL_CHILDCARE_LEAVE',
] as const;

/**
 * Maternity leave type is only for Minsk or Krakow (Polish office in app) with EC contract.
 */
export function canSelectMaternityLeaveType(
  officeId: string | number | undefined | null,
  contractType: string | undefined | null,
): boolean {
  if (officeId == null || contractType == null) {
    return false;
  }
  const oid = String(officeId);
  if (!isEmployeeContractByType(String(contractType))) {
    return false;
  }
  return isMinskOffice(oid) || isPolishOffice(oid);
}

export function maternitySubtypeOptionsForEmployee(
  officeId: string | number | undefined | null,
  contractType: string | undefined | null,
): Partial<typeof MATERNITY_LEAVE_SUB_TYPES> {
  if (!canSelectMaternityLeaveType(officeId, contractType)) {
    return {};
  }
  const oid = String(officeId);
  if (isMinskOffice(oid)) {
    return Object.fromEntries(
      BY_MATERNITY_SUBTYPE_KEYS.map(k => [k, MATERNITY_LEAVE_SUB_TYPES_WITH_HINTS[k]]),
    ) as Partial<typeof MATERNITY_LEAVE_SUB_TYPES>;
  }
  if (isPolishOffice(oid)) {
    return Object.fromEntries(
      PL_MATERNITY_SUBTYPE_KEYS.map(k => [k, MATERNITY_LEAVE_SUB_TYPES_WITH_HINTS[k]]),
    ) as Partial<typeof MATERNITY_LEAVE_SUB_TYPES>;
  }
  return {};
}

export function assertMaternityLeaveAllowed(
  officeId: string | number | undefined | null,
  contractType: string | undefined | null,
  type: string | undefined,
  subType: string | null | undefined,
): void {
  if (type !== LEAVE_TYPES_ALIASES.MATERNITY_LEAVE) {
    return;
  }
  if (!canSelectMaternityLeaveType(officeId, contractType)) {
    throw new Error('Maternity leave is only available for Minsk or Krakow employees with an EC contract.');
  }
  const allowedKeys = Object.keys(maternitySubtypeOptionsForEmployee(officeId, contractType));
  if (subType != null && subType !== '' && !allowedKeys.includes(subType)) {
    throw new Error('This maternity leave subtype is not available for this employee.');
  }
}
